// 정산관리 목록
import { ServiceTypeCode } from '@/services/settlement/settlementPromotion/types'

export interface Amount {
  originAmount: number
  discountedAmount: number
}

export interface BaseAmount {
  totalAmount: Amount
  inventoryAmount: Amount
  inboundAmount: Amount
  customerOutboundAmount: Amount
  storeOutboundAmount: Amount
  returnInboundAmount: Amount
  releaseAmount: Amount
  etcAdjustmentAmount: number
  // LOGISPRD-16794 실측비. BE 미배포 구간 응답에는 없을 수 있어 optional
  measurementAmount?: Amount
}

export interface SettlementAmountSummaryRes extends BaseAmount {
  settlementMonth: string
  settlementStartDate: string
  settlementEndDate: string
  cutoverMonth: string | null
}

export interface SettlementAmountSummaryReq {
  settlementMonth: string
  // LP9182 국가(전체/한국/일본) 필터 — country code('KR'|'JP'). 전체는 undefined(미전송). 'CN'/'' 금지(BE 400).
  country?: string
}

export interface SettlementStatisticsRes extends SettlementAmountSummaryRes {
  partnerId: string
  settlementClosedDateTime: string | null
  settlementClosedStatus: 'PENDING' | 'COMPLETED'
  /**
   * 정산 기준 변경 발효월 (FFMPRO-816, 5/11 매핑).
   * - null: cutover 미발효 (기존 정산 기준 — 구매확정일자/클레임완료일자)
   * - YearMonth (YYYY-MM): 이 월부터 신규 정산 기준 적용.
   *   FE 는 `settlementMonth >= cutoverMonth` 식으로 컬럼명 분기 + 캘린더 maxDate 분기에 사용
   */
  cutoverMonth: string | null
  promotionDiscountedAmount: number
  /**
   * 화면 노출용 프로모션 할인 (LOGISPRD-11385) — 프로모션 집계(일 단위 + 기간 단위) 합산.
   * 노출 flag 가 꺼져 있으면 null. 총액 역산인 promotionDiscountedAmount 를 쓰지 않는다.
   */
  promotionSavedAmount: number | null
  /** 합배송으로 아낀 금액 (LOGISPRD-10076) — 노출 flag 가 꺼져 있으면 null */
  combinedShippingSavedAmount: number | null
  // LP9182 — 파트너별 국가집합 코드 문자열(group_concat, 예 "KR,JP"·순서 미보장). FE 가 split 후 useGetCountries 로 code→name 매핑(§4.3).
  // 국가 필터와 무관하게 항상 전체 집합 노출(금액만 필터). null = 집계 없음.
  countries: string | null
}

// 정산관리 상세
export interface PartnerSettlementStatisticItem {
  settlementDate: string
  serviceType: ServiceTypeCode
  totalAmount: Amount
  brandId: string
  brandName: string
  productNumber: string
  productName: string
  // BE PartnerDailySettlementDetailResult 의 String? 계약 반영(nullable). scm-portal InboundDisplayRow.productOption? 와 정합
  productOption?: string
  productBarcode: string
  // LOGISPRD-14189 — 응답에 이미 실려 오는 sku_id. 화면 노출은 SETTLEMENT_DETAIL_SKU_VIEW_FLAG 가 정한다
  skuId?: string | null
  /** 입고 상세의 서버 그룹 기준. 구버전 응답은 필드가 없어 바코드 기준을 유지한다. */
  productAxis?: 'SKU' | 'BARCODE'
  quantity: number
  unitPrice: number
  discountedUnitPrice: number
  // LP8238 박스단위 입고 정산 — BE LLD §4.2 / §8.1 응답 필드 (수동 추가, OpenAPI regen 없음)
  // unitType 은 BE NFR06 (unit_type DEFAULT 'PCS' backfill) 로 항상 채워지나, 만일의 누락(legacy) 대비 optional 로 두고 집계의 else 분기에서 PCS 로 방어한다
  unitType?: 'BOX' | 'PCS'
  pcsPerUnit?: number | null // BOX row 만 값, PCS row 는 null
  // LP9182 — line-item 국가(EnumResult). 보관/고객출고/매장출고/반품은 실값(한국/일본)·필터 동작, 입고는 항상 한국(BE 가드). 단가관리(#442/#450) 동일 shape.
  // optional: BE 롤아웃 중 누락 방어 + 컬럼 valueGetter 가 `?.name ?? '-'` 로 null-safe. 집계 row/legacy fixture 와도 호환.
  country?: { code: string; name: string }
}

// INBOUND raw N row(BOX row + PCS row)를 서버의 productAxis와 settlementDate 기준으로 묶은 표시 row
// scm-portal 의 InboundDisplayRow 와 동일 구성
export interface AggregatedInboundRow {
  settlementDate: string
  brandId: string
  brandName: string
  productNumber: string
  productName: string
  productOption?: string // scm-portal InboundDisplayRow 와 동일하게 optional (BE String? 계약 — raw row 도 optional)
  productBarcode: string
  skuId?: string | null
  productAxis?: 'SKU' | 'BARCODE'
  totalAmount: Amount // 입고비 = BOX row + PCS row 금액 합
  totalQuantity: number // 총수량(PCS 환산) = pcsQuantity + boxQuantity × pcsPerUnit
  pcsQuantity?: number // PCS 수량 (PCS row 의 quantity)
  boxQuantity?: number // BOX 수량 (BOX row 의 quantity = 박스 개수)
  pcsPerUnit?: number | null // 입수량 (1 BOX 당 PCS), BOX row 만 값 존재
  pcsUnitPrice?: Amount // PCS 단가 (할인전 originAmount + 할인후 discountedAmount)
  boxUnitPrice?: Amount // BOX 단가 (할인전 + 할인후)
}

// LP8519 박스단위 입고 정산 feature flag 키. 집계(hook)와 컬럼 표시(table)가 동일 키를 참조하도록 단일 출처화 (오타 시 데이터/컬럼 불일치 방지)
export const INBOUND_SETTLEMENT_UNIT_TYPE_FLAG = 'feature.inboundSettlementUnitTypeEnabled' as const

// LP9182 MFS 파트너 정산관리 국가(전체/한국/일본) 차원 노출 flag 키. OFF 면 국가 드롭다운/컬럼 미노출 + country 미전송(레거시 동일).
// BE country 응답이 준비되기 전 환경에서 빈 컬럼/오동작 노출을 막는 가드. 드롭다운/컬럼/파라미터가 동일 키를 참조하도록 단일 출처화.
export const CATEGORY_GLOBAL_SETTLEMENT_FLAG = 'feature.categoryGlobalSettlement' as const

// LOGISPRD-14189 정산관리 상세 sku_id 노출 flag (object 타입의 boolean 필드). ON 이면 SKU ID 컬럼을 보이고 보관·입고·매장출고·프로모션 탭은 상품번호·옵션을 숨긴다
export const SETTLEMENT_DETAIL_SKU_VIEW_FLAG = { key: 'feature.skuIdTransition', objectKey: 'settlementDetailSkuView' } as const

// LOGISPRD-16794 정산관리 실측비 노출 flag (object 타입의 boolean 필드). ON 이면 요약 타일·목록 열·상세 탭에 실측비를 보인다
export const MFS_MEASUREMENT_VIEW_FLAG = { key: 'feature.mfsMeasurement', objectKey: 'settlementViewEnabled' } as const

type ServiceTypeItemMap = {
  ALL: {
    settlementDate: string
    promotionDiscountAmount: number
    // LP9182 — 일자별 국가집합 코드 문자열("KR,JP"). 필터연동: 전체→전체집합/국가선택→해당 코드/데이터 없는 일자→null. FE 가 code→name 매핑.
    countries: string | null
  } & BaseAmount
  INVENTORY: { logisticsCategory: string } & PartnerSettlementStatisticItem
  INBOUND: PartnerSettlementStatisticItem
  OUTBOUND_TO_CUSTOMER: {
    orderNumber: string
    orderOptionNumber: string
    invoiceNumber: string
    outboundAmount: Amount
    outboundShippingAmount: Amount
    remoteAreaShippingAmount: number
  } & PartnerSettlementStatisticItem
  OUTBOUND_TO_STORE: {
    outboundAmount: Amount
    outboundShippingAmount: Amount
  } & PartnerSettlementStatisticItem
  RETURN_INBOUND: {
    originOrderNumber: string
    originOrderOptionNumber: string
    invoiceNumber: string
    responsibleParty: {
      code: string
      name: string
    }
    returnInboundAmount: Amount
    returnShippingAmount: Amount
    returnRemoteAreaShippingAmount: number
    outboundRefundAmount: number
    outboundShippingRefundAmount: number
    outboundRemoteAreaShippingRefundAmount: number
    isFreeReturn: boolean
  } & PartnerSettlementStatisticItem
  ETC_ADJUSTMENT: {
    createdDate: string
    serviceType: ServiceTypeCode
    adjustmentAmount: number
    adjustmentReasonType: {
      code: string
      name: string
    }
    adjustmentChargeType: {
      code: AdjustmentChargeType
      name: string
    }
    settlementMonth: string
    adjustmentCount: number
    adjustmentReason: string
  }
  // LOGISPRD-16794 실측비 상세 행. 옵션·바코드는 화면에 쓰지 않는다
  MEASUREMENT: PartnerSettlementStatisticItem
}
export type ServiceTypeKey =
  | 'ALL'
  | Extract<
      ServiceTypeCode,
      'INVENTORY' | 'INBOUND' | 'OUTBOUND_TO_CUSTOMER' | 'OUTBOUND_TO_STORE' | 'RETURN_INBOUND' | 'MEASUREMENT' | 'ETC_ADJUSTMENT'
    >
export type SettlementStatisticDetailItem<ServiceType extends ServiceTypeKey> = ServiceTypeItemMap[ServiceType]

export type PartnerSettlementStatisticsReq = ApiPaginationReq<{
  partnerId: string
  settlementMonth: string
  serviceType: Exclude<ServiceTypeKey, 'ALL'>
  // LP9182 국가 필터 — code('KR'|'JP'). 전체는 undefined(미전송).
  country?: string
}>
export type DailySettlementStatisticsReq = Pick<PartnerSettlementStatisticsReq, 'partnerId' | 'settlementMonth' | 'country'>

export type DailySettlementStatisticsRes = SettlementStatisticDetailItem<'ALL'>
export type PartnerSettlementStatisticsRes = SettlementStatisticDetailItem<Exclude<ServiceTypeKey, 'ALL'>>

// 정산 마감
export interface SettlementConfirmReq extends SettlementAmountSummaryReq {
  partnerIds: string[]
}

export interface SettlementConfirmRes {
  settlementMonth: string
  closed: string[]
  alreadyClosed: string[]
  notFound: string[]
}

// 기타 조정 금액
export const ADJUSTMENT_CHARGE_TYPE = ['DEDUCTION', 'CHARGE'] as const
export const ADJUSTMENT_REASON_TYPE = [
  'MANUAL_ADJUST',
  'RETURN_COST',
  'DAMAGE_COMPENSATION',
  'PROMOTION_SUPPORT',
  'OTHER_SHIPPING',
  'VAS_WORK',
  'ETC',
] as const

export type AdjustmentChargeType = (typeof ADJUSTMENT_CHARGE_TYPE)[number]
export type AdjustmentReasonType = (typeof ADJUSTMENT_REASON_TYPE)[number]

export interface AdjustmentAmountReq {
  settlementBaseType: 'PARTNER' | 'MUSINSA_LOGISTICS'
  partnerId: string
  settlementMonth: string
  adjustmentReasonType: AdjustmentReasonType
  adjustmentCount?: number
  adjustmentChargeType: AdjustmentChargeType
  adjustmentAmount: number
  adjustmentReason?: string
}

export interface AdjustmentAmountRes extends AdjustmentAmountReq {
  id: string
  createdAt: string
  createdBy: string
}

export interface AdjustmentTypesRes {
  code: AdjustmentReasonType
  name: string
}

export interface SettlementStatisticsDetailSummaryRes extends Partial<Omit<SettlementStatisticDetailItem<ServiceTypeKey>, 'serviceType'>> {
  serviceType: ServiceTypeCode
  totalAmount: Amount
  totalQuantity: number
}
export interface SettlementStatisticsDetailSummaryReq {
  partnerId: string
  settlementMonth: string
  serviceType: ServiceTypeKey
  // LP9182 국가 필터 — code('KR'|'JP'). 전체는 undefined(미전송).
  country?: string
}
